import { getRailsClient, getRailsFormClient } from "sj-ui-toolkit/src/client";
import { downloadCsv } from "sj-ui-toolkit/src/utils/downloadFile";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TournamentAudienceUser {
  id?: number;
  directoryUuid?: string;
  email?: string;
}

export interface TournamentContentItem {
  id: string | number;
  type: string;
  optional?: boolean;
}

export interface TournamentLesson {
  id?: string | number;
  entityId?: string;
  latestLessonId?: string;
  lessonKey?: string;
  name?: string;
  duration?: number;
  type: string;
  required?: boolean;
  allLessonsIds?: string[];
  constraint?: "path_only" | "tournament_only" | null;
}

export interface TournamentData {
  id?: number;
  name?: string;
  summary?: string;
  startDate?: string | null;
  endDate?: string | null;
  audience?: TournamentAudienceUser[];
  isGlobal?: boolean;
  content?: TournamentContentItem[];
  contactInfo?: string;
  prizes?: string;
  ongoing?: boolean;
}

export interface TournamentFormState {
  basicInfo: {
    name: string;
    description: string;
  };
  audience: {
    users: TournamentAudienceUser[];
    isGlobal: boolean;
  };
  content: {
    selected: TournamentLesson[];
  };
  settings: {
    startDate: Date | string | null;
    endDate: Date | string | null;
    contactPersons: string;
    prizes: string;
  };
}

export interface TournamentParticipant {
  name?: string;
  email?: string;
  score?: number | string;
  completedExercises?: number;
  rating?: number;
  time?: string;
}

export interface ExperimentFilterData {
  languages?: Array<{ name: string }>;
  difficulties?: Array<{ name: string }>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const DEFAULT_TOURNAMENT_DESCRIPTION =
  "Join to compete against your peers! Points are awarded based on proficiency in each activity. Time to complete will be used as a tie-breaker. Good luck!";

// ─── API functions ────────────────────────────────────────────────────────────

export const fetchTournamentsAsync = async (): Promise<TournamentData[]> => {
  const API = "/api/internal_api/v2/admin/tournaments";
  const { data } = await getRailsClient().get<TournamentData[]>(API);
  return data ?? [];
};

export const deleteTournamentAsync = async (tournamentId: number | string): Promise<void> => {
  const API = `/api/internal_api/v2/admin/tournaments/${tournamentId}`;
  await getRailsClient().delete(API);
};

export const createTournamentAsync = async (payload: FormData): Promise<TournamentData> => {
  const API = "/api/internal_api/v2/admin/tournaments";
  const { data } = await getRailsFormClient().post<TournamentData>(API, payload, {
    headers: { "Content-Type": "multipart/form-data" },
    transformRequest: (d: unknown) => d,
  });
  return data;
};

export const updateTournamentAsync = async (
  tournamentId: number | string,
  payload: FormData
): Promise<TournamentData> => {
  const API = `/api/internal_api/v2/admin/tournaments/${tournamentId}`;
  const { data } = await getRailsFormClient().put<TournamentData>(API, payload, {
    headers: { "Content-Type": "multipart/form-data" },
    transformRequest: (d: unknown) => d,
  });
  return data;
};

export const fetchTournamentLessonsAsync = async ({
  tags = [],
  difficulty = [],
  lessonType = [],
  release = [],
  includeExperiments = true,
}: {
  tags?: string[];
  difficulty?: string[];
  lessonType?: string[];
  release?: string[];
  includeExperiments?: boolean;
} = {}): Promise<TournamentLesson[]> => {
  const EXPERIMENTS_API = "/api/internal_api/v1/admin/experiments?constraint=tournament_only";

  const hasExperimentOnly = lessonType.includes("experiment_only");
  const nonExperimentTypes = lessonType.filter(lt => lt !== "experiment_only");
  const isInitialLoad =
    tags.length === 0 && difficulty.length === 0 && lessonType.length === 0 && release.length === 0;

  const normalizeLessons = (lessons: TournamentLesson[]): TournamentLesson[] =>
    lessons.map(lesson => ({ ...lesson, type: "lesson" }));

  const normalizeExperiments = (experiments: TournamentLesson[]): TournamentLesson[] =>
    experiments
      .filter(experiment => experiment.constraint !== "path_only")
      .map(experiment => ({ ...experiment, type: "experiment" }));

  if (hasExperimentOnly && nonExperimentTypes.length === 0) {
    const { data } = await getRailsClient().get<{ experiments?: TournamentLesson[] }>(
      EXPERIMENTS_API
    );
    return normalizeExperiments(data.experiments ?? []);
  }

  const params = new URLSearchParams();
  params.append("query", "");
  params.append("constraint", "tournament_only");
  tags.forEach(tag => params.append("tags[]", tag));
  difficulty.forEach(d => params.append("difficulty[]", d));
  nonExperimentTypes.forEach(lt => params.append("lesson_type[]", lt));
  release.forEach(r => params.append("release[]", r));
  const lessonsUrl = `/api/internal_api/v1/admin/lessons?${params.toString()}`;

  const shouldIncludeExperiments = includeExperiments && (isInitialLoad || hasExperimentOnly);

  if (shouldIncludeExperiments) {
    const [lessonsRes, experimentsRes] = await Promise.all([
      getRailsClient().get<{ lessons?: TournamentLesson[] }>(lessonsUrl),
      getRailsClient().get<{ experiments?: TournamentLesson[] }>(EXPERIMENTS_API),
    ]);
    return [
      ...normalizeLessons(lessonsRes.data.lessons ?? []),
      ...normalizeExperiments(experimentsRes.data.experiments ?? []),
    ];
  }

  const { data } = await getRailsClient().get<{ lessons?: TournamentLesson[] }>(lessonsUrl);
  return normalizeLessons(data.lessons ?? []);
};

export const fetchExperimentDataForTournamentAsync = async (): Promise<ExperimentFilterData> => {
  const API = "/api/internal_api/v1/admin/experiments?constraint=tournament_only";
  const { data } = await getRailsClient().get<ExperimentFilterData>(API);
  return data ?? {};
};

export const fetchTournamentLeaderboardAsync = async (
  tournamentId: number | string
): Promise<{ participants: TournamentParticipant[] }> => {
  const BASE_URL = `/api/internal_api/v2/admin/tournaments/${tournamentId}/show_leaderboard`;
  let currentPage = 1;
  let allParticipants: TournamentParticipant[] = [];
  let pagesCount = 1;

  do {
    const { data } = await getRailsClient().get<{
      participants?: TournamentParticipant[];
      pagesCount?: number;
    }>(`${BASE_URL}?query=&current_page=${currentPage}`);
    allParticipants = allParticipants.concat(data.participants ?? []);
    pagesCount = data.pagesCount ?? 1;
    currentPage += 1;
  } while (currentPage <= pagesCount);

  return { participants: allParticipants };
};

export const exportTournamentLeaderboardAsync = async (
  tournamentId: number | string
): Promise<void> => {
  const API = `/api/internal_api/v2/admin/tournaments/${tournamentId}/download`;
  const { data } = await getRailsClient().get<string>(API);
  downloadCsv(data, `tournament-${tournamentId}-leaderboard.csv`);
};

export const buildTournamentFormData = (tournament: TournamentFormState): FormData => {
  const formData = new FormData();

  formData.append("tournament[name]", tournament.basicInfo.name);
  formData.append("tournament[summary]", tournament.basicInfo.description);

  if (tournament.settings.startDate) {
    formData.append("tournament[start_date]", String(tournament.settings.startDate));
  }
  if (tournament.settings.endDate) {
    formData.append("tournament[end_date]", String(tournament.settings.endDate));
  }

  formData.append("tournament[is_global]", String(tournament.audience.isGlobal));
  formData.append("tournament[prizes]", tournament.settings.prizes ?? "");
  formData.append("tournament[contact_info]", tournament.settings.contactPersons ?? "");
  formData.append("tournament[description]", "");
  formData.append("tournament[rules]", "");

  const content = tournament.content.selected.map(item => ({
    id: item.latestLessonId,
    type: item.type,
    optional: item.required === false,
  }));
  formData.append("tournament[content]", JSON.stringify(content));

  tournament.audience.users.forEach(user => {
    if (user.id) {
      formData.append("tournament[user_ids][]", String(user.id));
    }
    if (user.directoryUuid) {
      formData.append("tournament[directory_uuids][]", user.directoryUuid);
    }
  });

  return formData;
};
